import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import BlogCard from "../components/BlogCard";
import { blogPosts } from "../data/blogs";

// reading time helper
function getReadingTime(text) {
  const words = text.split(/\s+/).length;
  return Math.ceil(words / 200);
}

export default function BlogList() {
  const [search, setSearch] = useState("");
  const [activeTag, setActiveTag] = useState("All");

  // extract all tags
  const allTags = ["All", ...new Set(blogPosts.flatMap(p => p.tags || []))];

  const filteredPosts = useMemo(() => {
    return blogPosts.filter((post) => {
      const matchesSearch = post.title
        .toLowerCase()
        .includes(search.toLowerCase());

      const matchesTag =
        activeTag === "All" || post.tags?.includes(activeTag);

      return matchesSearch && matchesTag;
    });
  }, [search, activeTag]);

  const featured = filteredPosts[0];
  const rest = filteredPosts.slice(1);

  return (
    <section className="relative py-32 bg-slate-50 overflow-hidden">

      {/* Background */}
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-blue-100/40 blur-3xl"></div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">

        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-sm uppercase tracking-widest text-blue-600 font-semibold mb-4">
            Project Journal
          </p>

          <h1 className="text-4xl md:text-5xl font-semibold text-slate-900">
            Engineering Logs
          </h1>

          <p className="text-slate-600 mt-6 max-w-2xl mx-auto">
            Real development process, decisions, and system architecture breakdowns.
          </p>
        </div>

        {/* Search */}
        <div className="mb-10 max-w-xl mx-auto">
          <input
            type="text"
            placeholder="Search logs..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-white border border-slate-200 rounded-2xl px-5 py-3 focus:ring-2 focus:ring-blue-500/40"
          />
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-3 justify-center mb-16">
          {allTags.map((tag) => (
            <button
              key={tag}
              onClick={() => setActiveTag(tag)}
              className={`px-4 py-2 rounded-full text-sm transition
                ${
                  activeTag === tag
                    ? "bg-blue-600 text-white"
                    : "bg-white border border-slate-200 text-slate-600 hover:border-blue-400"
                }
              `}
            >
              {tag}
            </button>
          ))}
        </div>

        {/* Featured */}
        {featured && (
          <div className="mb-24">
            <p className="text-sm uppercase text-slate-500 mb-6">
              Featured Log
            </p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <BlogCard
                post={{
                  ...featured,
                  readingTime: getReadingTime(featured.content),
                }}
                featured
              />
            </motion.div>
          </div>
        )}

        {/* Grid */}
        {rest.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-12">
            {rest.map((post, index) => (
              <motion.div
                key={post.slug}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.06 }}
              >
                <BlogCard
                  post={{
                    ...post,
                    readingTime: getReadingTime(post.content),
                  }}
                />
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center text-slate-500 mt-20">
            No logs found for this filter.
          </div>
        )}

      </div>
    </section>
  );
}